// Lightweight celebration overlay with banner, confetti, and fireworks
// Pure DOM + Canvas; no external deps. Designed to be invoked at tournament completion.

export interface ChampionCelebrationOptions {
  tournamentName: string;
  championName: string;
  durationMs?: number; // default ~12s
}

let celebrationActive = false;

export function showChampionCelebration(opts: ChampionCelebrationOptions) {
  if (celebrationActive) return; // prevent stacking
  celebrationActive = true;

  const { tournamentName, championName, durationMs = 12000 } = opts;

  // Root overlay
  const root = document.createElement('div');
  root.id = 'champion-celebration';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-live', 'polite');
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:12000',
    'pointer-events:auto',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:flex-start',
    'background:rgba(0,0,0,0.25)',
  ].join(';');

  // Canvas for confetti + fireworks
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
  const ctx = canvas.getContext('2d');

  // Banner container
  const banner = document.createElement('div');
  banner.style.cssText = [
    'position:relative',
    'margin-top:6vh',
    'padding:20px 28px',
    'border-radius:18px',
    'backdrop-filter:blur(6px)',
    'background:linear-gradient(180deg, rgba(10,10,10,0.65), rgba(10,10,10,0.4))',
    'border:1px solid rgba(255,255,255,0.12)',
    'box-shadow:0 12px 50px rgba(0,0,0,0.45), inset 0 0 30px rgba(255,255,255,0.06)',
    'text-align:center',
    'color:#fff',
    'animation:champion-pop 600ms cubic-bezier(.2,.9,.2,1)',
    'max-width:92vw',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = `${tournamentName} Champion!`;
  title.style.cssText = [
    'font-weight:900',
    'letter-spacing:.02em',
    'font-size:min(6vw, 48px)',
    'text-shadow:0 2px 0 rgba(0,0,0,.6), 0 4px 18px rgba(255,215,0,.35)',
    'background:linear-gradient(92deg, #ffd700, #ffae00, #fff2a8)',
    '-webkit-background-clip:text',
    'background-clip:text',
    'color:transparent',
  ].join(';');

  const subtitle = document.createElement('div');
  subtitle.textContent = `🏆 ${championName}`;
  subtitle.style.cssText = [
    'margin-top:6px',
    'font-weight:800',
    'font-size:min(4.5vw, 28px)',
    'color:#fff',
    'text-shadow:0 1px 10px rgba(255,255,255,.4)',
  ].join(';');

  // Close button (optional)
  const close = document.createElement('button');
  close.type = 'button';
  close.ariaLabel = 'Dismiss celebration';
  close.textContent = '×';
  close.style.cssText = [
    'position:absolute',
    'top:10px',
    'right:14px',
    'width:36px',
    'height:36px',
    'border-radius:10px',
    'border:1px solid rgba(255,255,255,0.2)',
    'background:rgba(0,0,0,0.45)',
    'color:#fff',
    'font-size:22px',
    'line-height:34px',
    'cursor:pointer',
  ].join(';');

  banner.appendChild(title);
  banner.appendChild(subtitle);
  banner.appendChild(close);

  // Style keyframes
  const style = document.createElement('style');
  style.textContent = `
@keyframes champion-pop { from { transform:translateY(-16px) scale(.96); opacity:.0 } to { transform:translateY(0) scale(1); opacity:1 } }
`;

  // Build DOM
  root.appendChild(canvas);
  root.appendChild(banner);
  document.head.appendChild(style);
  document.body.appendChild(root);

  // Resize canvas
  const resize = () => {
    if (!ctx) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const rect = root.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(root);

  // Simple particle systems
  type Confetti = { x:number; y:number; vx:number; vy:number; w:number; h:number; rot:number; vr:number; color:string; t:number };
  type Spark = { x:number; y:number; vx:number; vy:number; life:number; color:string };
  const confetti: Confetti[] = [];
  const sparks: Spark[] = [];

  const colors = ['#ff3b3b','#ffcc00','#33dd66','#66ccff','#ff66cc','#a188ff'];

  // Seed confetti
  const seedConfetti = (count:number) => {
    for (let i=0;i<count;i++) {
      confetti.push({
        x: Math.random()*canvas.clientWidth,
        y: -20 - Math.random()*100,
        vx: (Math.random()-0.5)*0.6,
        vy: 1 + Math.random()*1.5,
        w: 6 + Math.random()*5,
        h: 10 + Math.random()*8,
        rot: Math.random()*Math.PI*2,
        vr: (Math.random()*0.15 + 0.05) * (Math.random()<0.5?-1:1),
        color: colors[(Math.random()*colors.length)|0],
        t: 0,
      });
    }
  };

  // Firework burst
  const burst = (x?:number, y?:number) => {
    const bx = x ?? (canvas.clientWidth*0.15 + Math.random()*canvas.clientWidth*0.7);
    const by = y ?? (canvas.clientHeight*0.25 + Math.random()*canvas.clientHeight*0.35);
    const n = 60 + (Math.random()*40)|0;
    const baseColor = colors[(Math.random()*colors.length)|0];
    for (let i=0;i<n;i++) {
      const ang = (i / n) * Math.PI*2 + Math.random()*0.2;
      const spd = 2 + Math.random()*3.5;
      sparks.push({
        x: bx,
        y: by,
        vx: Math.cos(ang)*spd,
        vy: Math.sin(ang)*spd - 0.5,
        life: 60 + Math.random()*40,
        color: baseColor,
      });
    }
  };

  seedConfetti(160);
  // Stagger fireworks
  let lastBurst = 0;
  const burstInterval = 900; // ms

  let alive = true;
  const tStart = performance.now();

  const step = (now:number) => {
    if (!ctx || !alive) return;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    ctx.clearRect(0,0,w,h);

    // Confetti update
    for (let i=0;i<confetti.length;i++) {
      const c = confetti[i];
      c.t += 1;
      c.x += c.vx + Math.sin(c.t*0.05)*0.5;
      c.y += c.vy;
      c.rot += c.vr;
      if (c.y > h + 30) {
        c.y = -20; c.x = Math.random()*w; c.t = 0; // recycle
      }
    }

    // Fireworks spawn
    if (now - lastBurst > burstInterval) {
      burst();
      lastBurst = now;
    }

    // Sparks update
    for (let i=sparks.length-1;i>=0;i--) {
      const s = sparks[i];
      s.vx *= 0.985; s.vy = s.vy*0.985 + 0.03; // drag + gravity
      s.x += s.vx; s.y += s.vy;
      s.life -= 1;
      if (s.life <= 0) sparks.splice(i,1);
    }

    // Draw confetti
    for (const c of confetti) {
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(c.rot);
      ctx.fillStyle = c.color;
      ctx.fillRect(-c.w/2, -c.h/2, c.w, c.h);
      ctx.restore();
    }

    // Draw sparks (additive glow)
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const s of sparks) {
      // Convert hex color like #ff3b3b to rgba
      const hr = s.color.replace('#','');
      const r = parseInt(hr.substring(0,2), 16);
      const gch = parseInt(hr.substring(2,4), 16);
      const b = parseInt(hr.substring(4,6), 16);
      const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, 8);
      g.addColorStop(0, `rgba(${r},${gch},${b},0.85)`);
      g.addColorStop(1, `rgba(${r},${gch},${b},0.00)`);
      ctx.fillStyle = g as any;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 8, 0, Math.PI*2);
      ctx.fill();
    }
    ctx.restore();

    // Stop condition
    if (now - tStart > durationMs) {
      fadeOut();
      return;
    }

    req = requestAnimationFrame(step);
  };

  // Fade and cleanup
  const fadeOut = () => {
    alive = false;
    let op = 1;
    root.style.transition = 'opacity 500ms ease';
    root.style.opacity = '0';
    setTimeout(() => cleanup(), 520);
  };

  const cleanup = () => {
    try { ro.disconnect(); } catch {}
    try { if (req) cancelAnimationFrame(req); } catch {}
    if (root.parentElement) root.parentElement.removeChild(root);
    if (style.parentElement) style.parentElement.removeChild(style);
    celebrationActive = false;
  };

  // Interactions
  close.onclick = () => fadeOut();
  root.addEventListener('click', (e) => {
    // Clicking empty backdrop also closes
    if (e.target === root) fadeOut();
  });

  let req = requestAnimationFrame(step);
}
